import { Component } from '@theme/component';
import { ResizeNotifier } from '@theme/utilities';
import { SizeSelectedEvent } from '@theme/events';

/**
 * SizePicker – metafield based size selector
 * - NO variant fetching
 * - NO URL mutation
 * - NO section morphing
 */
export default class SizePicker extends Component {
  /** @type {number[]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[]} */
  #radios = [];

  #resizeObserver = new ResizeNotifier(() => this.updateSizePickerCss());

  connectedCallback() {
    super.connectedCallback();

    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios = [...radios];

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices = [initialCheckedIndex];
      }
    });

    this.addEventListener('change', this.onSizeChange.bind(this));
    this.#resizeObserver.observe(this);

    this.updateSizePickerCss();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
  }

  /**
   * Handle size change
   * @param {Event} event
   */
  onSizeChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLElement)) return;

    this.updateSelectedOption(target);

    const sizeValue =
      target instanceof HTMLSelectElement
        ? target.value
        : target.dataset.optionValueId;

    if (!sizeValue) return;

    /**
     * Custom event for theme / app usage
     * detail.size => selected size string
     */
    this.updateSelectedSize(sizeValue);
    this.dispatchEvent(new SizeSelectedEvent({ size: sizeValue }));
  }

  /**
   * Update selected size in UI
   * @param {string} sizeValue
   */
  updateSelectedSize(sizeValue) {
    const selectedSizeElement = this.querySelector('[data-selected-size]');
    if (selectedSizeElement) {
      selectedSizeElement.textContent = sizeValue;
    }
  }

  /**
   * Update radio / select checked states
   * @param {HTMLElement} target
   */
  updateSelectedOption(target) {
    if (target instanceof HTMLInputElement) {
      const inputIndex = Number(target.dataset.inputIndex);

      if (Number.isNaN(inputIndex)) return;

      const radios = this.#radios;
      const checked = this.#checkedIndices;

      if (!radios || !checked) return;

      const [currentIndex, previousIndex] = checked;

      if (currentIndex !== undefined && radios[currentIndex]) {
        radios[currentIndex].dataset.currentChecked = 'false';
      }

      if (previousIndex !== undefined && radios[previousIndex]) {
        radios[previousIndex].dataset.previousChecked = 'false';
      }

      checked.unshift(inputIndex);
      checked.length = Math.min(checked.length, 2);

      const [newCurrent, newPrevious] = checked;

      if (newCurrent !== undefined && radios[newCurrent]) {
        radios[newCurrent].dataset.currentChecked = 'true';
        radios[newCurrent].checked = true;
      }

      if (newPrevious !== undefined && radios[newPrevious]) {
        radios[newPrevious].dataset.previousChecked = 'true';
      }

      this.updateFieldsetCss();
    }

    if (target instanceof HTMLSelectElement) {
      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      target.selectedOptions[0]?.setAttribute('selected', 'selected');
    }
  }

  /**
   * Update pill animation CSS
   */
  updateSizePickerCss() {}

  updateFieldsetCss() {
    this.updateSizePickerCss();
  }
}

if (!customElements.get('size-picker')) {
  customElements.define('size-picker', SizePicker);
}
